'use client';
import { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  user_id: string;
  type: 'order_status' | 'commission_payout' | 'admin_alert' | 'new_assignment';
  title: string;
  message: string;
  is_read: boolean;
  metadata: Record<string, any>;
  created_at: string;
}

type NotificationListener = () => void;

interface UserNotificationStore {
  notifications: Notification[];
  loading: boolean;
  listeners: Set<NotificationListener>;
  refCount: number;
  channel: RealtimeChannel | null;
}

const stores = new Map<string, UserNotificationStore>();
const realtimeLocks = new Map<string, Promise<void>>();

function getStore(userId: string): UserNotificationStore {
  let store = stores.get(userId);
  if (!store) {
    store = {
      notifications: [],
      loading: false,
      listeners: new Set(),
      refCount: 0,
      channel: null,
    };
    stores.set(userId, store);
  }
  return store;
}

function emit(store: UserNotificationStore) {
  store.listeners.forEach((listener) => listener());
}

async function fetchStoreNotifications(
  userId: string,
  supabase: ReturnType<typeof createClient>,
  force = false
) {
  let store = getStore(userId);
  if (store.loading && !force) return;
  store.loading = true;
  emit(store);
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (!error && data) {
      store.notifications = data as Notification[];
    }
  } catch (err) {
    console.error('[useNotifications] fetch error:', err);
  } finally {
    store.loading = false;
    emit(store);
  }
}

async function teardownRealtime(
  userId: string,
  supabase: ReturnType<typeof createClient>
) {
  let store = stores.get(userId);
  if (!store?.channel) return;
  const channel = store.channel;
  store.channel = null;
  await supabase.removeChannel(channel);
}

async function ensureRealtime(
  userId: string,
  supabase: ReturnType<typeof createClient>
) {
  let store = getStore(userId);
  if (store.channel) return;

  let lock = realtimeLocks.get(userId);
  if (!lock) {
    lock = (async () => {
      if (store.channel || store.refCount === 0) return;

      await teardownRealtime(userId, supabase);
      if (store.refCount === 0) return;

      const channel = supabase
        .channel(`notifications:${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload: { eventType: string; new: Record<string, unknown> }) => {
            if (payload.eventType === 'INSERT') {
              store.notifications = [
                payload.new as unknown as Notification,
                ...store.notifications,
              ].slice(0, 50);
            } else if (payload.eventType === 'UPDATE') {
              const updated = payload.new as unknown as Notification;
              store.notifications = store.notifications.map((n) =>
                n.id === updated.id ? updated : n
              );
            }
            emit(store);
          }
        )
        .subscribe();

      if (store.refCount === 0) {
        await supabase.removeChannel(channel);
        return;
      }

      store.channel = channel;
    })();
    realtimeLocks.set(userId, lock);
  }

  try {
    await lock;
  } finally {
    if (realtimeLocks.get(userId) === lock) {
      realtimeLocks.delete(userId);
    }
  }
}

function subscribeStore(userId: string, listener: NotificationListener) {
  let store = getStore(userId);
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

function acquireNotifications(userId: string) {
  const supabase = createClient();
  let store = getStore(userId);
  const isFirstSubscriber = store.refCount === 0;
  store.refCount += 1;

  if (isFirstSubscriber) {
    void fetchStoreNotifications(userId, supabase);
    void ensureRealtime(userId, supabase).catch((err) => {
      console.error('[useNotifications] realtime setup error:', err);
    });
  } else {
    emit(store);
  }

  return () => {
    store.refCount -= 1;
    if (store.refCount <= 0) {
      store.refCount = 0;
      void teardownRealtime(userId, supabase).finally(() => {
        if (store.refCount === 0) {
          stores.delete(userId);
        }
      });
    }
  };
}

export function useNotifications() {
  const supabase = createClient();
  const { user } = useAuth();
  const userId = user?.id;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    let store = getStore(userId);
    const sync = () => {
      setNotifications(store.notifications);
      setLoading(store.loading);
    };

    sync();
    const unsubscribe = subscribeStore(userId, sync);
    const release = acquireNotifications(userId);

    return () => {
      unsubscribe();
      release();
    };
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    await fetchStoreNotifications(userId, supabase, true);
  }, [userId, supabase]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!userId) return;
      try {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', notificationId)
          .eq('user_id', userId);
        let store = getStore(userId);
        store.notifications = store.notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        );
        emit(store);
      } catch (err) {
        console.error('[useNotifications] markAsRead error:', err);
      }
    },
    [userId, supabase]
  );

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);
      let store = getStore(userId);
      store.notifications = store.notifications.map((n) => ({ ...n, is_read: true }));
      emit(store);
    } catch (err) {
      console.error('[useNotifications] markAllAsRead error:', err);
    }
  }, [userId, supabase]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}
